import { useState } from 'react';
import { trackEvent } from '../utils/analytics';

/**
 * ShareButton Component
 *
 * Provides sharing functionality with Web Share API (mobile) and clipboard fallback (desktop)
 *
 * Props:
 * - status: { isDayForIt: boolean, reason: string } - Current weather status
 * - weather: { temperature_2m: number, wind_speed_10m: number } - Weather data
 */
function ShareButton({ status, weather }) {
  const [showCopied, setShowCopied] = useState(false);
  const [shareError, setShareError] = useState('');
  const shareStatusId = 'share-status-message';
  const shareErrorId = 'share-error-message';

  const generateShareText = () => {
    if (!status) return "Check out Day For It - see if it's park or pub weather!";

    const verdict = status.isDayForIt ? "It's a DAY FOR IT! ☀️🍻" : "NOT a day for it... pub time! 🌧️🍺";
    const temp = weather ? `${weather.temperature_2m}°C` : '';
    const wind = weather ? `wind ${weather.wind_speed_10m} km/h` : '';
    const conditions = weather ? ` (${temp}, ${wind})` : '';

    return `${verdict}${conditions}\n\n${status.reason}\n\nCheck it out:`;
  };

  const handleShare = async () => {
    trackEvent('share_button_click', {
      verdict: status?.isDayForIt ? 'day_for_it' : 'not_a_day_for_it',
      method: navigator.share ? 'web_share' : 'clipboard',
    });

    const shareData = {
      title: 'Day For It?',
      text: generateShareText(),
      url: window.location.href,
    };

    // Check if Web Share API is supported (mostly mobile browsers)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setShareError('');
        trackEvent('share_success', {
          method: 'web_share',
          verdict: status?.isDayForIt ? 'day_for_it' : 'not_a_day_for_it',
        });
        console.log('Shared successfully');
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          fallbackCopyToClipboard(shareData);
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API (desktop)
      fallbackCopyToClipboard(shareData);
    }
  };

  const fallbackCopyToClipboard = (shareData) => {
    const textToCopy = `${shareData.text}\n${shareData.url}`;

    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setShareError('');
          trackEvent('share_success', {
            method: 'clipboard',
            verdict: status?.isDayForIt ? 'day_for_it' : 'not_a_day_for_it',
          });
          showCopiedMessage();
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
          // Fallback to older method
          legacyCopyToClipboard(textToCopy);
        });
    } else {
      // Fallback for older browsers
      legacyCopyToClipboard(textToCopy);
    }
  };

  const legacyCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      setShareError('');
      showCopiedMessage();
    } catch (err) {
      console.error('Failed to copy:', err);
      setShareError('Copy failed. Please copy the link manually.');
    }

    document.body.removeChild(textArea);
  };

  const showCopiedMessage = () => {
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const handleSocialShare = (platform) => {
    trackEvent('share_social_click', {
      platform,
      verdict: status?.isDayForIt ? 'day_for_it' : 'not_a_day_for_it',
    });

    const shareText = generateShareText();
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(shareText);

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        // Facebook share dialog
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        // Twitter/X share
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'whatsapp':
        // WhatsApp share
        shareUrl = `https://wa.me/?text=${text}%20${url}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="share-button-container">
      {/* Primary share button - Web Share API / Copy to clipboard */}
      <button
        type="button"
        onClick={handleShare}
        className="share-button share-button-primary"
        aria-label={showCopied ? 'Copied to clipboard!' : 'Share with mates'}
        aria-describedby={showCopied ? shareStatusId : shareError ? shareErrorId : undefined}
      >
        {showCopied ? (
          <>
            <span className="share-icon" aria-hidden="true">✓</span>
            Copied to clipboard!
          </>
        ) : (
          <>
            <span className="share-icon" aria-hidden="true">📤</span>
            Share with mates
          </>
        )}
      </button>

      {showCopied && (
        <p
          id={shareStatusId}
          className="share-hint"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Paste the link to your friends!
        </p>
      )}

      {shareError && (
        <p
          id={shareErrorId}
          className="share-hint"
          role="alert"
        >
          {shareError}
        </p>
      )}

      {/* Social media share buttons */}
      <div className="social-share-buttons">
        <button
          type="button"
          onClick={() => handleSocialShare('facebook')}
          className="social-share-btn facebook"
          aria-label="Share on Facebook"
          title="Share on Facebook"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true" focusable="false">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </button>

        <button
          type="button"
          onClick={() => handleSocialShare('twitter')}
          className="social-share-btn twitter"
          aria-label="Share on Twitter/X"
          title="Share on Twitter/X"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true" focusable="false">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </button>

        <button
          type="button"
          onClick={() => handleSocialShare('whatsapp')}
          className="social-share-btn whatsapp"
          aria-label="Share on WhatsApp"
          title="Share on WhatsApp"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true" focusable="false">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

export default ShareButton;
